import classNames from 'clsx';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Selection } from '@swf/sports/components';
import { useEventMarketTemplatesWidget } from '@swf/sports/models/market-templates/eventBased';
import type {
  MarketLevelCompoundMarketTemplateProps,
  MarketTemplateComponent
} from '@swf/sports/types/templates';
import { notEmpty } from '@swf/sports/utils/notEmpty';

import styles from './GameLines.scss';

const GameLines: MarketTemplateComponent<MarketLevelCompoundMarketTemplateProps> = ({
  marketIds
}) => {
  const { widgetSelect } = useEventMarketTemplatesWidget();
  const allMarkets = useSelector(widgetSelect.markets);
  const events = useSelector(widgetSelect.events);

  const {
    overUnderMarket,
    moneyLineMarket,
    handicapMarket,
    awayMoneyLineRunner,
    awayHandicapRunner,
    homeMoneyLineRunner,
    homeHandicapRunner,
    overRunner,
    underRunner
  } = useMemo(() => {
    const mktIds = new Set(marketIds.filter(notEmpty));
    const filteredMarkets = Object.values(allMarkets).filter(
      (market) => market && mktIds.has(market.marketId)
    );

    const overUnderMarket = filteredMarkets.find(
      (mkt) => mkt?.marketType === 'TOTAL_POINTS_(OVER/UNDER)'
    );
    const moneyLineMarket = filteredMarkets.find((mkt) => mkt?.marketType === 'MONEY_LINE');
    const handicapMarket = filteredMarkets.find(
      (mkt) => mkt?.marketType === 'MATCH_HANDICAP_(2-WAY)'
    );

    return {
      overUnderMarket,
      moneyLineMarket,
      handicapMarket,
      awayMoneyLineRunner: moneyLineMarket?.runners.find((runner) => runner.resultType === 'AWAY'),
      awayHandicapRunner: handicapMarket?.runners.find((runner) => runner.resultType === 'AWAY'),
      homeMoneyLineRunner: moneyLineMarket?.runners.find((runner) => runner.resultType === 'HOME'),
      homeHandicapRunner: handicapMarket?.runners.find((runner) => runner.resultType === 'HOME'),
      overRunner: overUnderMarket?.runners.find((runner) => runner.resultType === 'OVER'),
      underRunner: overUnderMarket?.runners.find((runner) => runner.resultType === 'UNDER')
    };
  }, [allMarkets, marketIds]);

  return (
    <table
      className={classNames(styles.table, {
        [styles.participantMarket]: (moneyLineMarket || handicapMarket) && !overUnderMarket,
        [styles.pointsMarket]: !moneyLineMarket && !handicapMarket && overUnderMarket,
        [styles.allParticipantMarkets]: moneyLineMarket && handicapMarket && !overUnderMarket,
        [styles.participantAndPointsMarkets]:
          (handicapMarket || moneyLineMarket) && overUnderMarket,
        [styles.allMarkets]: handicapMarket && moneyLineMarket && overUnderMarket
      })}
    >
      <thead>
        <tr className={styles.header}>
          <td className={styles.hidden} />
          {moneyLineMarket ? <th scope="col">{moneyLineMarket.marketName}</th> : null}
          {handicapMarket ? <th scope="col">{handicapMarket.marketName}</th> : null}
          {overUnderMarket ? (
            <th className={styles.overUnderHeader} scope="col">
              {overUnderMarket.marketName}
            </th>
          ) : null}
        </tr>
      </thead>
      <tbody>
        {moneyLineMarket || handicapMarket ? (
          <>
            <tr className={styles.away}>
              <th scope="row">
                {awayMoneyLineRunner?.runnerName || awayHandicapRunner?.runnerName}
              </th>
              {moneyLineMarket && awayMoneyLineRunner ? (
                <td>
                  <Selection
                    competitionId={moneyLineMarket.competitionId}
                    eventId={moneyLineMarket.eventId}
                    eventTypeId={moneyLineMarket.eventTypeId}
                    handicap={awayMoneyLineRunner.handicap}
                    legTypes={moneyLineMarket.legTypes}
                    marketId={moneyLineMarket.marketId}
                    marketStatus={moneyLineMarket.marketStatus}
                    marketType={moneyLineMarket.marketType}
                    odds={awayMoneyLineRunner.winRunnerOdds}
                    runnerResultType={awayMoneyLineRunner.resultType}
                    runnerStatus={awayMoneyLineRunner.runnerStatus}
                    selectionId={awayMoneyLineRunner.selectionId}
                    startTime={events[moneyLineMarket.eventId].eventStartTime}
                  />
                </td>
              ) : null}
              {handicapMarket && awayHandicapRunner ? (
                <td>
                  <Selection
                    competitionId={handicapMarket.competitionId}
                    eventId={handicapMarket.eventId}
                    eventTypeId={handicapMarket.eventTypeId}
                    handicap={awayHandicapRunner.handicap}
                    legTypes={handicapMarket.legTypes}
                    marketId={handicapMarket.marketId}
                    marketStatus={handicapMarket.marketStatus}
                    marketType={handicapMarket.marketType}
                    odds={awayHandicapRunner.winRunnerOdds}
                    runnerResultType={awayHandicapRunner.resultType}
                    runnerStatus={awayHandicapRunner.runnerStatus}
                    selectionId={awayHandicapRunner.selectionId}
                    startTime={events[handicapMarket.eventId].eventStartTime}
                  />
                </td>
              ) : null}
              <td className={styles.hidden} />
            </tr>

            <tr className={styles.home}>
              <th scope="row">
                {homeMoneyLineRunner?.runnerName || homeHandicapRunner?.runnerName}
              </th>
              {moneyLineMarket && homeMoneyLineRunner ? (
                <td>
                  <Selection
                    competitionId={moneyLineMarket.competitionId}
                    eventId={moneyLineMarket.eventId}
                    eventTypeId={moneyLineMarket.eventTypeId}
                    handicap={homeMoneyLineRunner.handicap}
                    legTypes={moneyLineMarket.legTypes}
                    marketId={moneyLineMarket.marketId}
                    marketStatus={moneyLineMarket.marketStatus}
                    marketType={moneyLineMarket.marketType}
                    odds={homeMoneyLineRunner.winRunnerOdds}
                    runnerResultType={homeMoneyLineRunner.resultType}
                    runnerStatus={homeMoneyLineRunner.runnerStatus}
                    selectionId={homeMoneyLineRunner.selectionId}
                    startTime={events[moneyLineMarket.eventId].eventStartTime}
                  />
                </td>
              ) : null}
              {handicapMarket && homeHandicapRunner ? (
                <td>
                  <Selection
                    competitionId={handicapMarket.competitionId}
                    eventId={handicapMarket.eventId}
                    eventTypeId={handicapMarket.eventTypeId}
                    handicap={homeHandicapRunner.handicap}
                    legTypes={handicapMarket.legTypes}
                    marketId={handicapMarket.marketId}
                    marketStatus={handicapMarket.marketStatus}
                    marketType={handicapMarket.marketType}
                    odds={homeHandicapRunner.winRunnerOdds}
                    runnerResultType={homeHandicapRunner.resultType}
                    runnerStatus={homeHandicapRunner.runnerStatus}
                    selectionId={homeHandicapRunner.selectionId}
                    startTime={events[handicapMarket.eventId].eventStartTime}
                  />
                </td>
              ) : null}
              <td className={styles.hidden} />
            </tr>
          </>
        ) : null}

        {overUnderMarket ? (
          <tr className={styles.over}>
            <th scope="row">Over</th>
            {moneyLineMarket ? <td className={styles.hidden} /> : null}
            {handicapMarket ? <td className={styles.hidden} /> : null}
            <td>
              {overRunner ? (
                <Selection
                  competitionId={overUnderMarket.competitionId}
                  eventId={overUnderMarket.eventId}
                  eventTypeId={overUnderMarket.eventTypeId}
                  handicap={overRunner.handicap}
                  legTypes={overUnderMarket.legTypes}
                  marketId={overUnderMarket.marketId}
                  marketStatus={overUnderMarket.marketStatus}
                  marketType={overUnderMarket.marketType}
                  odds={overRunner.winRunnerOdds}
                  runnerResultType={overRunner.resultType}
                  runnerStatus={overRunner.runnerStatus}
                  selectionId={overRunner.selectionId}
                  startTime={events[overUnderMarket.eventId].eventStartTime}
                />
              ) : null}
            </td>
          </tr>
        ) : null}

        {overUnderMarket ? (
          <tr className={styles.under}>
            <th scope="row">Under</th>
            {moneyLineMarket ? <td className={styles.hidden} /> : null}
            {handicapMarket ? <td className={styles.hidden} /> : null}
            <td>
              {underRunner ? (
                <Selection
                  competitionId={overUnderMarket.competitionId}
                  eventId={overUnderMarket.eventId}
                  eventTypeId={overUnderMarket.eventTypeId}
                  handicap={underRunner.handicap}
                  legTypes={overUnderMarket.legTypes}
                  marketId={overUnderMarket.marketId}
                  marketStatus={overUnderMarket.marketStatus}
                  marketType={overUnderMarket.marketType}
                  odds={underRunner.winRunnerOdds}
                  runnerResultType={underRunner.resultType}
                  runnerStatus={underRunner.runnerStatus}
                  selectionId={underRunner.selectionId}
                  startTime={events[overUnderMarket.eventId].eventStartTime}
                />
              ) : null}
            </td>
          </tr>
        ) : null}
      </tbody>
    </table>
  );
};

GameLines.marketTemplateType = 'EVENT';

export default GameLines;
